/**
 * فیلترِ واژه‌های ناپسند — WHOSUP-309، بندِ 1.2ی بازبینیِ اپل.
 *
 * ═══ چه می‌کند ═══
 *
 * متنِ سیگنال، پیامِ چت و نامِ نمایه پیش از رفتن به دیتابیس از این‌جا رد
 * می‌شود. اگر واژه‌ای از فهرست در آن باشد، **جلویش گرفته می‌شود** (تصمیمِ
 * صاحبِ محصول: جلوگیری، نه هشدار) و کاربر می‌تواند همان‌جا اعتراض کند —
 * اعتراض در `content_filter_appeals` (مایگریشن 0160) می‌نشیند و به تلگرام
 * خبر می‌دهد.
 *
 * ═══ چرا فقط در کلاینت ═══
 *
 * این فیلتر سدِ اول است، نه تنها سد: گزارش، بلاک و رسیدگیِ ادمین پشتش‌اند.
 * گذاشتنش در SQL یعنی فهرستِ ۱۳ زبانه در دو جا، و هر اصلاحِ «بی‌گناه را
 * گرفت» یک مایگریشن. این‌جا با `npm test` سنجیده می‌شود و با یک OTA عوض.
 *
 * ═══ چطور می‌سنجد ═══
 *
 * متن یکدست می‌شود (حروفِ کوچک، NFKC، بی‌اعراب، بی نیم‌فاصله و کشیده،
 * «ي/ك» عربی → «ی/ک» فارسی، اعدادِ جایگزینِ حرف مثلِ `sh1t`، و حرفِ تکراری
 * مثلِ `fuuuck`). بعد:
 *   • زبان‌های فاصله‌دار: هر **واژه‌ی کامل** با فهرست مقایسه می‌شود، و
 *     ریشه‌هایی که با `*` تمام می‌شوند پیشوندی (`fuck*` → fucking). این
 *     همان چیزی است که جلوی «Scunthorpe» را می‌گیرد: `class` و `assistant`
 *     هیچ‌کدام واژه‌ی `ass` نیستند.
 *   • چینی و کانتونی که فاصله ندارند: زیررشته.
 *
 * ⚠ فهرست عمداً کوتاه و تند است — فحشِ صریح، توهینِ نژادی/جنسیتی و واژه‌های
 * جنسیِ صریح. واژه‌ی «مرزی» (احمق، لعنتی) این‌جا نیست: جلوگیری از آن‌ها
 * آزارِ کاربرِ عادی است و کارِ گزارش است، نه فیلتر.
 */

export type FilterField = 'signal' | 'message' | 'profile_name';

/**
 * فهرست، به تفکیکِ زبان. همه با هم روی هر متنی سنجیده می‌شوند (کاربرِ
 * فارسی‌زبان هم ممکن است انگلیسی فحش بدهد).
 *
 * `*` در انتها = پیشوندی. بدون `*` = فقط واژه‌ی کامل.
 */
export const BLOCKLIST: Record<string, readonly string[]> = {
  en: [
    'fuck*', 'motherfuck*', 'shit', 'shitty', 'bullshit', 'cunt*', 'bitch*', 'whore*', 'slut*',
    'dick', 'dickhead', 'cock', 'cocksuck*', 'pussy', 'asshole*', 'bastard', 'wank*',
    'nigger*', 'nigga*', 'faggot*', 'fag', 'retard', 'retarded', 'tranny', 'kike', 'spic', 'chink',
    'paki', 'rape', 'rapist', 'porn*', 'blowjob*', 'cumshot*', 'dildo*', 'kys',
  ],
  fr: [
    'putain', 'pute', 'salope', 'connard', 'connasse', 'enculé', 'encule', 'enculer', 'nique',
    'niquer', 'fdp', 'chatte', 'pédé', 'pede', 'bougnoule', 'négro', 'negro', 'tapette',
  ],
  es: [
    'puta', 'puto', 'putas', 'cabrón', 'cabron', 'coño', 'gilipollas', 'joder',
    'mierda', 'maricón', 'maricon', 'polla', 'verga', 'pendejo', 'chinga*', 'culero',
  ],
  de: [
    'fotze', 'hurensohn', 'hure', 'arschloch', 'wichser', 'schlampe', 'scheiße', 'scheisse',
    'schwuchtel', 'kanake', 'missgeburt', 'ficken', 'fick', 'fickt',
  ],
  sv: [
    'fitta', 'kuk', 'knulla', 'jävla', 'javla', 'bög', 'blatte', 'neger', 'horunge',
  ],
  tl: [
    'puta', 'putangina', 'putang', 'tangina', 'gago', 'kantot*', 'pokpok', 'tarantado',
  ],
  fa: [
    'کیر*', 'کس', 'کسکش', 'کس‌کش', 'کونی', 'جنده', 'جاکش', 'مادرجنده', 'کسخل', 'کونده',
    'گاییدم', 'بگا', 'گایید*', 'سکس', 'حرومزاده', 'حرامزاده', 'لاشی', 'پفیوز',
  ],
  ar: [
    'كس', 'كسمك', 'شرموطة', 'شرموطه', 'قحبة', 'قحبه', 'منيوك', 'زب', 'نيك', 'ينيك',
    'متناك', 'لوطي', 'خول', 'عرص', 'ابن الكلب',
  ],
  ur: [
    'بہن چود', 'بہنچود', 'مادرچود', 'چوتیا', 'رنڈی', 'گانڈو', 'حرامی', 'لوڑا', 'بھڑوا',
  ],
  hi: [
    'चूतिया', 'भोसड़ी', 'मादरचोद', 'बहनचोद', 'रंडी', 'गांडू', 'हरामी', 'लौड़ा', 'भड़वा',
    'madarchod', 'behenchod', 'bhenchod', 'chutiya', 'randi', 'gandu', 'bhosdi*', 'lauda',
  ],
  pa: [
    'ਭੈਣਚੋਦ', 'ਮਾਂਚੋਦ', 'ਕੁੱਤੀ', 'ਰੰਡੀ', 'ਗਾਂਡੂ', 'ਹਰਾਮੀ', 'ਲੌੜਾ',
  ],
  zh: [
    '操你妈', '肏你妈', '傻逼', '煞笔', '婊子', '贱人', '鸡巴', '屄', '妈的', '他妈的', '去死', '尼哥',
  ],
  yue: [
    '屌你', '屌你老母', '冚家鏟', '仆街', '撚', '閪', '戇鳩', '含撚',
  ],
};

/** زبان‌هایی که واژه‌هایشان فاصله ندارند و زیررشته سنجیده می‌شوند. */
const SUBSTRING_LANGS = new Set(['zh', 'yue']);

const LEET: Record<string, string> = {
  '0': 'o', '1': 'i', '3': 'e', '4': 'a', '5': 's', '7': 't', '@': 'a', '$': 's', '!': 'i',
};

/**
 * یکدست‌سازی. هم روی متنِ کاربر و هم روی خودِ فهرست اجرا می‌شود، پس هر
 * نوشتاری از یک واژه به یک شکل می‌رسد.
 */
export function normalizeForFilter(text: string): string {
  return text
    .normalize('NFKC')
    .toLowerCase()
    .normalize('NFD').replace(/[̀-ͯ]/g, '')       // اعرابِ لاتین (é → e)
    .replace(/[ً-ٰٟ]/g, '')                   // اعرابِ عربی/فارسی
    .replace(/[‌‍ـ]/g, '')                    // نیم‌فاصله، اتصال، کشیده
    .replace(/ي/g, 'ی').replace(/ك/g, 'ک')
    // عدد به حرف فقط داخلِ واژه‌ای که حرف هم دارد (sh1t)؛ «5:30» و شماره‌ی
    // تلفن عدد می‌مانند و واژه‌ای نمی‌سازند.
    .replace(/\S+/g, (w) => (/\p{L}/u.test(w) ? w.replace(/[013457@$!]/g, (c) => LEET[c] ?? c) : w))
    .replace(/(\p{L})\1{2,}/gu, '$1');                       // fuuuck → fuck (سه‌تا و بیشتر)
}

type Entry = { raw: string; norm: string; prefix: boolean; substring: boolean; phrase: boolean };

const ENTRIES: Entry[] = Object.entries(BLOCKLIST).flatMap(([lang, words]) =>
  words.map((raw) => {
    const prefix = raw.endsWith('*');
    const norm = normalizeForFilter(prefix ? raw.slice(0, -1) : raw);
    return { raw, norm, prefix, substring: SUBSTRING_LANGS.has(lang), phrase: /\s/.test(norm) };
  }));

/** واژه‌ها: هر رشته‌ی پیوسته از حرف و نشانه (برای هندی و پنجابی، نشانه‌ها بخشِ واژه‌اند). */
const tokens = (norm: string): string[] => norm.match(/[\p{L}\p{M}]+/gu) ?? [];

/**
 * واژه‌های ناپسندی که در متن پیدا شد — شکلِ فهرست، بی‌تکرار. خالی یعنی آزاد.
 *
 * چیزی که برمی‌گردد همان است که در اعتراض (`matched`) ذخیره می‌شود، تا
 * ادمین ببیند کدام واژه گیر داد.
 */
export function findObjectionable(text: string | null | undefined): string[] {
  if (!text) return [];
  const norm = normalizeForFilter(text);
  const words = tokens(norm);
  const spaced = ` ${words.join(' ')} `;
  const hits = new Set<string>();
  for (const e of ENTRIES) {
    if (!e.norm) continue;
    if (e.substring) {
      if (norm.includes(e.norm)) hits.add(e.raw);
    } else if (e.phrase) {
      if (spaced.includes(` ${e.norm} `)) hits.add(e.raw);
    } else if (e.prefix) {
      if (words.some((w) => w.startsWith(e.norm))) hits.add(e.raw);
    } else if (words.includes(e.norm)) {
      hits.add(e.raw);
    }
  }
  return [...hits];
}

/** آیا این متن را می‌شود فرستاد. */
export const isCleanText = (text: string | null | undefined): boolean =>
  findObjectionable(text).length === 0;

/**
 * چند متن با هم (مثلاً عنوان و توضیحِ سیگنال) — یک فهرستِ واحد از هرچه گیر داد.
 */
export function findObjectionableIn(...texts: (string | null | undefined)[]): string[] {
  return [...new Set(texts.flatMap((t) => findObjectionable(t)))];
}

/** خطایی که مسیرهای نوشتن پرتاب می‌کنند تا رابط پیامِ «جلوگیری شد + اعتراض» نشان دهد. */
export class ObjectionableContentError extends Error {
  readonly field: FilterField;
  readonly matched: string[];
  readonly body: string;
  constructor(field: FilterField, matched: string[], body: string) {
    super('objectionable_content');
    this.name = 'ObjectionableContentError';
    this.field = field;
    this.matched = matched;
    this.body = body;
  }
}

export const isObjectionableError = (e: unknown): e is ObjectionableContentError =>
  e instanceof ObjectionableContentError
  || (typeof e === 'object' && e !== null && (e as { name?: string }).name === 'ObjectionableContentError');

/** اگر متن ناپسند است پرتاب کن — برای سرِ مسیرهای نوشتنِ repoها. */
export function assertClean(field: FilterField, ...texts: (string | null | undefined)[]): void {
  assertCleanExcept([], field, ...texts);
}

/**
 * همان `assertClean`، با واژه‌هایی (شکلِ فهرست، مثلِ `fuck*`) که برای این
 * کاربر آزاد شده‌اند — یعنی ادمین اعتراضش را «اشتباه گرفت» زده
 * (`assertCleanForMe` در `reportRepo`).
 */
export function assertCleanExcept(
  exempt: readonly string[], field: FilterField, ...texts: (string | null | undefined)[]
): void {
  const matched = findObjectionableIn(...texts).filter((w) => !exempt.includes(w));
  if (matched.length) {
    throw new ObjectionableContentError(field, matched, texts.filter(Boolean).join('\n'));
  }
}
